const PUSH_STATE_KEY = 'iteroBrowserPush';

// Stored in location.state on the entries the browser pushed. depth counts our pushes since the
// last entry with the modal closed; null when the chain started from a link that already had it open.
export interface IteroPushMarker {
    view: string;
    depth: number | null;
}

export type IteroHistoryState = Readonly<Record<typeof PUSH_STATE_KEY, IteroPushMarker>>;

export type IteroHistoryStep = { kind: 'pop'; delta: number } | { kind: 'replace' };

const REPLACE: IteroHistoryStep = { kind: 'replace' };

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

export function readPushMarker(state: unknown): IteroPushMarker | null {
    if (!isRecord(state)) return null;
    const marker = state[PUSH_STATE_KEY];
    if (!isRecord(marker) || typeof marker.view !== 'string') return null;
    const depth = typeof marker.depth === 'number' && marker.depth > 0 ? marker.depth : null;
    return { view: marker.view, depth };
}

export function pushState(
    current: IteroPushMarker | null,
    wasOpen: boolean,
    nextView: string
): IteroHistoryState {
    const depth = !wasOpen ? 1 : current?.depth ? current.depth + 1 : null;
    return { [PUSH_STATE_KEY]: { view: nextView, depth } };
}

// Pops only while the entry still shows the view we pushed; a tab switch or a dropped param
// since then means the entry before it isn't this view's parent.
export function stepBackDecision(
    marker: IteroPushMarker | null,
    currentView: string
): IteroHistoryStep {
    return marker !== null && marker.view === currentView ? { kind: 'pop', delta: -1 } : REPLACE;
}

export function closeModalDecision(marker: IteroPushMarker | null): IteroHistoryStep {
    return marker?.depth ? { kind: 'pop', delta: -marker.depth } : REPLACE;
}
